import React, { useContext } from "react";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { AuthContext } from "../../context/AuthContext";
import {
  meetsFeature,
  featureMinTier,
  TIER_LABEL,
  FEATURES,
} from "../../config/tiers";
import { useUpgrade } from "./UpgradeContext";

/**
 * Soft lock. When the user's tier can access `feature`, renders children
 * normally. Otherwise renders them blurred + non-interactive under a glass
 * panel with an upgrade button (opens the shared UpgradeModal).
 */
const FeatureGate = ({ feature, children }) => {
  const { profile } = useContext(AuthContext);
  const { promptUpgrade } = useUpgrade();
  const tier = profile?.tier || "basic";

  if (meetsFeature(tier, feature)) return children;

  const minTier = featureMinTier(feature);
  const def = FEATURES[feature] || {};
  const label = def.label || "This feature";

  return (
    <div className="relative min-h-[24rem]">
      <div className="pointer-events-none select-none blur-md opacity-40" aria-hidden>
        {children}
      </div>
      <div className="absolute inset-0 flex items-start justify-center pt-16 px-4">
        <div className="text-center max-w-sm rounded-2xl bg-white/85 dark:bg-[#0f1624]/85 backdrop-blur-xl border border-gray-200 dark:border-white/10 shadow-glass dark:shadow-glass-dark p-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/15 via-blue-500/15 to-purple-500/15 mb-4">
            <LockClosedIcon className="w-5 h-5 text-indigo-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {label} is a {TIER_LABEL[minTier]} feature
          </h3>
          {def.modalCopy?.body && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 leading-relaxed">
              {def.modalCopy.body}
            </p>
          )}
          <button
            type="button"
            onClick={() => promptUpgrade(feature)}
            className="mt-5 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors"
          >
            Upgrade to {TIER_LABEL[minTier]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureGate;
